<template>
  <controller>
    <page-header title="Pessoas" />

    <main class="people-page">
      <section class="filter-panel" aria-labelledby="people-filters-title">
        <div class="panel-heading">
          <div>
            <h2 id="people-filters-title">Buscar pessoas</h2>
            <p>Pesquise por nome, e-mail, ID ou CPF e combine os perfis necessários.</p>
          </div>
          <button type="button" class="btn btn-secondary" :disabled="loading || !hasFilters" @click="clearFilters">
            Limpar filtros
          </button>
        </div>

        <form class="filters" @submit.prevent="applyFilters">
          <label class="search-field">
            <span>Busca</span>
            <input v-model.trim="filters.search" type="search" minlength="3" maxlength="150" placeholder="Nome ou e-mail" autocomplete="off">
          </label>
          <label>
            <span>ID da pessoa</span>
            <input v-model="filters.person_id" type="number" min="1" placeholder="Ex.: 12540">
          </label>
          <label>
            <span>CPF</span>
            <input v-model.trim="filters.cpf" type="text" maxlength="14" inputmode="numeric" placeholder="000.000.000-00">
          </label>
          <label>
            <span>Situação do colaborador</span>
            <select v-model="filters.collaborator_status">
              <option value="">Todas</option>
              <option value="active">Ativo</option>
              <option value="inactive">Inativo</option>
              <option value="mixed">Misto</option>
              <option value="missing">Sem vínculo</option>
            </select>
          </label>
          <label>
            <span>Situação do usuário</span>
            <select v-model="filters.user_status">
              <option value="">Todas</option>
              <option value="active">Ativo</option>
              <option value="inactive">Inativo</option>
              <option value="absent">Sem usuário</option>
              <option value="conflict">Múltiplos</option>
              <option value="legacy">Status legado</option>
            </select>
          </label>
          <fieldset class="profile-filter">
            <legend>Perfis</legend>
            <label v-for="option in profileOptions" :key="option.value" class="check-option">
              <input v-model="filters.profiles" type="checkbox" :value="option.value">
              <span>{{ option.label }}</span>
            </label>
          </fieldset>
          <label class="check-option anomaly-filter">
            <input v-model="filters.anomalies_only" type="checkbox">
            <span>Somente com revisão necessária</span>
          </label>
          <div class="filter-actions">
            <button type="submit" class="btn btn-primary" :disabled="loading || invalidSearch">
              {{ loading ? 'Buscando...' : 'Aplicar filtros' }}
            </button>
          </div>
        </form>
        <p v-if="invalidSearch" class="field-error" role="alert">Digite ao menos três caracteres na busca textual.</p>
      </section>

      <form-spinner v-if="loading" aria-label="Carregando pessoas" />

      <section v-else-if="loadError" class="state-panel error" role="alert">
        <h2>Não foi possível carregar as pessoas</h2>
        <p>{{ loadError }}</p>
        <button type="button" class="btn btn-secondary" @click="loadPeople">Tentar novamente</button>
      </section>

      <section v-else-if="!people.length" class="state-panel">
        <h2>Nenhuma pessoa encontrada</h2>
        <p>Ajuste os filtros e tente novamente.</p>
      </section>

      <section v-else class="results-panel" aria-labelledby="people-results-title">
        <div class="results-heading">
          <div>
            <h2 id="people-results-title">Resultado</h2>
            <p aria-live="polite">{{ pagination.total }} pessoa(s) encontrada(s)</p>
          </div>
          <label class="page-size">
            <span>Por página</span>
            <select v-model.number="filters.per_page" @change="changePage(1)">
              <option :value="25">25</option>
              <option :value="50">50</option>
              <option :value="100">100</option>
            </select>
          </label>
        </div>

        <div class="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Pessoa</th>
                <th>Perfis</th>
                <th>Colaborador</th>
                <th>Usuário</th>
                <th class="actions-heading">Ação</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="person in people" :key="person.idPessoa">
                <td>
                  <strong>{{ person.name }}</strong>
                  <small>ID {{ person.idPessoa }} · {{ person.email || 'Sem e-mail' }}</small>
                  <small>{{ person.cpf || 'CPF não informado' }}</small>
                </td>
                <td>
                  <div class="chips">
                    <span v-for="profile in person.profiles" :key="profile" class="profile-chip">{{ profileLabel(profile) }}</span>
                    <span v-if="person.has_anomalies" class="profile-chip warning">Revisar</span>
                  </div>
                </td>
                <td><span :class="['status-badge', person.collaborator_status]">{{ collaboratorStatusLabel(person.collaborator_status) }}</span></td>
                <td><span :class="['status-badge', person.user_status]">{{ userStatusLabel(person.user_status) }}</span></td>
                <td class="row-actions">
                  <router-link class="btn btn-primary btn-small" :to="{ name: 'Pessoa', params: { idPessoa: person.idPessoa }, query: { from: routeQuery } }">
                    Abrir
                  </router-link>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="mobile-list">
          <article v-for="person in people" :key="`mobile-${person.idPessoa}`">
            <header>
              <div><strong>{{ person.name }}</strong><small>ID {{ person.idPessoa }}</small></div>
              <span v-if="person.has_anomalies" class="profile-chip warning">Revisar</span>
            </header>
            <p>{{ person.email || 'Sem e-mail' }}</p>
            <div class="chips"><span v-for="profile in person.profiles" :key="profile" class="profile-chip">{{ profileLabel(profile) }}</span></div>
            <footer>
              <router-link class="btn btn-primary" :to="{ name: 'Pessoa', params: { idPessoa: person.idPessoa }, query: { from: routeQuery } }">Abrir pessoa</router-link>
            </footer>
          </article>
        </div>

        <nav class="pagination" aria-label="Paginação de pessoas">
          <button type="button" class="btn btn-secondary" :disabled="pagination.current_page <= 1" @click="changePage(pagination.current_page - 1)">Anterior</button>
          <span>Página {{ pagination.current_page }} de {{ pagination.last_page }}</span>
          <button type="button" class="btn btn-secondary" :disabled="pagination.current_page >= pagination.last_page" @click="changePage(pagination.current_page + 1)">Próxima</button>
        </nav>
      </section>
    </main>
  </controller>
</template>

<script>
import PessoasService from '@/services/pessoas.service'

const defaultFilters = () => ({
  search: '',
  person_id: '',
  cpf: '',
  profiles: [],
  collaborator_status: '',
  user_status: '',
  anomalies_only: false,
  per_page: 25,
  page: 1
})

export default {
  name: 'Pessoas',
  data () {
    return {
      people: [],
      loading: false,
      loadError: '',
      filters: defaultFilters(),
      pagination: { total: 0, current_page: 1, last_page: 1 },
      profileOptions: [
        { value: 'student', label: 'Aluno' },
        { value: 'collaborator', label: 'Colaborador' },
        { value: 'user', label: 'Usuário' }
      ]
    }
  },
  computed: {
    hasFilters () {
      return Boolean(this.filters.search || this.filters.person_id || this.filters.cpf || this.filters.profiles.length || this.filters.collaborator_status || this.filters.user_status || this.filters.anomalies_only)
    },
    invalidSearch () {
      return this.filters.search.length > 0 && this.filters.search.length < 3
    },
    routeQuery () {
      return btoa(unescape(encodeURIComponent(JSON.stringify(this.cleanParams()))))
    }
  },
  mounted () {
    this.restoreFilters()
    this.loadPeople()
  },
  methods: {
    restoreFilters () {
      const encoded = this.$route.query.filters
      if (!encoded) return
      try {
        this.filters = { ...defaultFilters(), ...JSON.parse(decodeURIComponent(escape(atob(encoded)))) }
      } catch (error) {
        this.filters = defaultFilters()
      }
    },
    cleanParams () {
      return Object.fromEntries(Object.entries(this.filters).filter(([, value]) => {
        if (Array.isArray(value)) return value.length
        return value !== '' && value !== false && value !== null
      }))
    },
    async loadPeople () {
      if (this.invalidSearch) return
      this.loading = true
      this.loadError = ''
      try {
        const response = await PessoasService.list(this.cleanParams())
        const page = response.data.data
        this.people = page.data || []
        this.pagination = {
          total: page.total || 0,
          current_page: page.current_page || 1,
          last_page: page.last_page || 1
        }
        await this.$router.replace({ query: { filters: this.routeQuery } })
      } catch (error) {
        this.loadError = error.response?.data?.message || 'Verifique sua conexão e suas permissões.'
      } finally {
        this.loading = false
      }
    },
    applyFilters () {
      this.filters.page = 1
      this.loadPeople()
    },
    clearFilters () {
      this.filters = defaultFilters()
      this.loadPeople()
    },
    changePage (page) {
      this.filters.page = page
      this.loadPeople()
    },
    profileLabel (profile) {
      return { person: 'Pessoa', student: 'Aluno', collaborator: 'Colaborador', user: 'Usuário' }[profile] || profile
    },
    collaboratorStatusLabel (status) {
      return { active: 'Ativo', inactive: 'Inativo', mixed: 'Misto', unknown: 'Sem situação', absent: 'Sem vínculo' }[status] || status
    },
    userStatusLabel (status) {
      return { active: 'Ativo', inactive: 'Inativo', registered: 'Cadastrado', conflict: 'Múltiplos', legacy: 'Legado', absent: 'Sem usuário' }[status] || status
    }
  }
}
</script>

<style lang="scss" scoped>
.people-page { max-width: 1440px; margin: 0 auto; padding: $mg_mid; color: $color-dev; }
.filter-panel, .results-panel, .state-panel { margin-bottom: $mg_mid; padding: $hmg_mid; background: $color-secondary; border: $border-component; border-radius: $border-radius; }
.panel-heading, .results-heading { display: flex; align-items: center; justify-content: space-between; gap: $mg_small; }
.panel-heading h2, .results-heading h2, .state-panel h2 { margin: 0 0 $hmg_mini; font-size: 18px; }
.panel-heading p, .results-heading p, .state-panel p { margin: 0; color: $color-text; }
.filters { display: grid; grid-template-columns: minmax(260px, 2fr) repeat(4, minmax(150px, 1fr)); gap: $mg_small; margin-top: $hmg_mid; align-items: end; }
label, fieldset { margin: 0; }
label:not(.check-option), .page-size { display: flex; flex-direction: column; gap: $hmg_mini; font-weight: 600; }
input, select { width: 100%; min-height: 44px; padding: 0 $mg_small; border: $border-component; border-radius: $border-radius-mid; background: $color-secondary; color: $color-dev; }
input:focus-visible, select:focus-visible, button:focus-visible, a:focus-visible { outline: 3px solid $color-success; outline-offset: 2px; }
.search-field { grid-column: span 2; }
.profile-filter { grid-column: span 3; display: flex; align-items: center; flex-wrap: wrap; gap: $mg_small; min-height: 44px; padding: $hmg_mini $mg_small; border: $border-component; border-radius: $border-radius-mid; }
.profile-filter legend { width: auto; margin: 0 $mg_mini 0 0; font-size: 13px; font-weight: 700; }
.check-option { display: inline-flex; align-items: center; gap: $hmg_mini; font-weight: 600; }
.check-option input { width: 20px; min-height: 20px; }
.anomaly-filter { min-height: 44px; }
.filter-actions { display: flex; justify-content: flex-end; }
.field-error { margin: $mg_mini 0 0; color: $color-error; }
.state-panel { text-align: center; padding: $mg; }
.state-panel.error { border-color: $color-error !important; }
.results-heading { margin-bottom: $hmg_small; }
.page-size { min-width: 120px; }
.table-wrap { overflow-x: auto; }
table { width: 100%; border-collapse: collapse; }
th, td { padding: $mg_small; border-bottom: $border-component; text-align: left; vertical-align: middle; }
th { color: $color-text; font-size: $font-size-small; }
td strong, td small { display: block; }
td small { margin-top: $hmg_mini; color: $color-text; }
.chips { display: flex; flex-wrap: wrap; gap: $hmg_mini; }
.profile-chip, .status-badge { display: inline-flex; align-items: center; min-height: 28px; padding: 0 $mg_mini; border-radius: $border-radius-xlarge; background: $color-bg; border: $border-component; font-size: $font-size-small; font-weight: 700; }
.profile-chip.warning, .status-badge.conflict, .status-badge.legacy, .status-badge.mixed { border-color: $color-warning !important; }
.status-badge.active { background: $color-active-light; }
.status-badge.inactive { background: $color-bg; }
.actions-heading { text-align: center; }
.row-actions { text-align: center; }
.btn-small { min-height: 36px; padding: $hmg_mini $mg_mini; }
.pagination { display: flex; align-items: center; justify-content: center; gap: $mg_small; margin-top: $hmg_mid; }
.mobile-list { display: none; }
@media (max-width: 1100px) {
  .people-page { padding: $hmg_mid; }
  .filters { grid-template-columns: 1fr 1fr; }
  .search-field, .profile-filter { grid-column: 1 / -1; }
  .table-wrap { display: none; }
  .mobile-list { display: grid; gap: $mg_small; }
  .mobile-list article { padding: $hmg_small; border: $border-component; border-radius: $border-radius-mid; }
  .mobile-list header { display: flex; justify-content: space-between; gap: $mg_small; }
  .mobile-list header small { display: block; margin-top: $hmg_mini; color: $color-text; }
  .mobile-list p { color: $color-text; overflow-wrap: anywhere; }
  .mobile-list footer { margin-top: $mg_small; }
}
@media (max-width: 640px) {
  .people-page { padding: $mg_small; }
  .panel-heading, .results-heading { align-items: stretch; flex-direction: column; }
  .filters { grid-template-columns: 1fr; }
  .search-field, .profile-filter { grid-column: auto; }
  .profile-filter { align-items: flex-start; flex-direction: column; }
  .filter-actions .btn, .panel-heading .btn, .mobile-list footer .btn { width: 100%; min-height: 44px; }
  .pagination { flex-wrap: wrap; }
}
</style>
